import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import DownloadKpsCreator from '../../../components/ui/DownloadKpsCreator'; // <-- Import de votre bouton dynamique
import { Home, Building, MapPin, Store, Calendar, Bed, Bath, Square, Shield, Zap, Droplets, Wifi, Wind, Trees, Car, ArrowUp, Check, Lock } from 'lucide-react';

const PropertyDetails = ({ property }) => {
  const formatPrice = (price, marketType, rentalPeriod) => {
    const formattedPrice = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(price);
    
    if (marketType === 'rental') {
      const periodSuffix = rentalPeriod === 'monthly' ? ' / mois' : rentalPeriod === 'yearly' ? ' / an' : '';
      return `${formattedPrice}${periodSuffix}`;
    }
    return formattedPrice;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getPropertyTypeIcon = (type) => {
    const typeLower = type?.toLowerCase();
    if (typeLower === 'villa' || typeLower === 'house') return <Home size={24} className="text-brand-gold" />;
    if (typeLower === 'apartment') return <Building size={24} className="text-brand-gold" />;
    if (typeLower === 'commercial') return <Store size={24} className="text-brand-gold" />;
    return <MapPin size={24} className="text-brand-gold" />;
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: { color: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20', text: 'Disponible' },
      reserved: { color: 'bg-brand-gold/10 text-brand-gold border border-brand-gold/20', text: 'Réservé' },
      sold: { color: 'bg-red-500/10 text-red-400 border border-red-500/20', text: 'Vendu' },
      withdrawn: { color: 'bg-white/5 text-white/40 border border-white/10', text: 'Retiré' }
    };
    return badges?.[status] || badges?.available;
  };

  const getMarketTypeLabel = (marketType) => {
    return marketType === 'rental' ? 'À louer' : marketType === 'sale' ? 'À vendre' : 'Propriété';
  };

  if (!property) {
    return (
      <div className="bg-[#111111] rounded-3xl p-8 shadow-2xl border-none outline-none">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-white/5 rounded-xl w-3/4"></div>
          <div className="h-4 bg-white/5 rounded-lg w-1/2"></div>
          <div className="h-8 bg-white/5 rounded-xl w-1/3 mt-8"></div>
        </div>
      </div>
    );
  }

  const statusBadge = getStatusBadge(property?.status);

  return (
    <div className="bg-[#111111] rounded-3xl shadow-2xl border-none outline-none overflow-hidden relative">
      
      {/* Reflet de fond subtil */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold/5 blur-[80px] pointer-events-none rounded-full" />

      {/* Header */}
      <div className="p-8 pb-6 relative z-10">
        <div className="flex flex-col md:flex-row items-start justify-between gap-6 mb-8">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 flex items-center justify-center border border-brand-gold/20">
                {getPropertyTypeIcon(property?.property_type)}
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white leading-tight tracking-tight">
                {property?.title}
              </h1>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 text-white/50 mb-6 font-light">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-brand-gold" />
                <span className="text-base">{property?.address}</span>
              </div>
              {property?.communes?.name && (
                <>
                  <span className="text-white/20">•</span>
                  <span className="text-base text-white/70">{property?.communes?.name}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-3">
              <span className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                property?.market_type === 'rental' ? 'bg-white/10 text-white' : property?.market_type === 'sale' ? 'bg-brand-gold text-black shadow-[0_0_10px_rgba(212,175,55,0.2)]' : 'bg-white/5 text-white/60'
              }`}>
                {getMarketTypeLabel(property?.market_type)}
              </span>
              <span className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${statusBadge?.color}`}>
                {statusBadge?.text}
              </span>
            </div>
          </div>
        </div>

        {/* Price Block */}
        <div className="mb-10 pb-10 border-b border-white/5">
          <p className="text-4xl md:text-5xl font-heading font-bold text-brand-gold mb-2 tracking-tight">
            {formatPrice(property?.price, property?.market_type, property?.rental_period)}
          </p>
          {property?.surface_area && (
            <p className="text-sm text-white/40 font-light">
              Soit environ {formatPrice(property?.price / property?.surface_area, property?.market_type, property?.rental_period)} / m²
            </p>
          )}
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {property?.bedrooms && (
            <div className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-2xl border-none hover:bg-white/10 transition-colors">
              <Bed size={24} className="text-brand-gold mb-3" />
              <p className="font-heading font-bold text-2xl text-white">{property?.bedrooms}</p>
              <p className="text-[10px] text-white/40 uppercase tracking-widest mt-1 font-bold">Chambres</p>
            </div>
          )}
          {property?.bathrooms && (
            <div className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-2xl border-none hover:bg-white/10 transition-colors">
              <Bath size={24} className="text-brand-gold mb-3" />
              <p className="font-heading font-bold text-2xl text-white">{property?.bathrooms}</p>
              <p className="text-[10px] text-white/40 uppercase tracking-widest mt-1 font-bold">Salles d'eau</p>
            </div>
          )}
          {property?.surface_area && (
            <div className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-2xl border-none hover:bg-white/10 transition-colors">
              <Square size={24} className="text-brand-gold mb-3" />
              <p className="font-heading font-bold text-2xl text-white">{property?.surface_area} <span className="text-sm text-white/50">m²</span></p>
              <p className="text-[10px] text-white/40 uppercase tracking-widest mt-1 font-bold">Surface</p>
            </div>
          )}
          <div className="flex flex-col items-center justify-center p-6 bg-white/5 rounded-2xl border-none hover:bg-white/10 transition-colors">
            <Calendar size={24} className="text-brand-gold mb-3" />
            <p className="font-heading font-bold text-xl text-white capitalize">{property?.property_type}</p>
            <p className="text-[10px] text-white/40 uppercase tracking-widest mt-1 font-bold">Type</p>
          </div>
        </div>
      </div>

      {/* Property Images */}
      {property?.images && property?.images?.length > 0 && (
        <div className="px-8 mb-12 relative z-10">
          <h3 className="font-heading font-bold text-lg text-white/80 uppercase tracking-widest mb-6">
            Aperçu de la propriété
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {property?.images?.slice(0, 6)?.map((image, index) => (
              <div key={index} className="aspect-video overflow-hidden rounded-2xl bg-[#050505] border-none group cursor-pointer">
                <Image
                  src={image}
                  alt={`${property?.title} Photo ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-80 group-hover:opacity-100"
                />
              </div>
            ))}
          </div>
          {property?.images?.length > 6 && (
            <div className="mt-8 text-center">
              <button className="text-[11px] font-bold uppercase tracking-widest text-brand-gold hover:text-black bg-brand-gold/10 hover:bg-brand-gold px-8 py-3 rounded-full transition-all duration-300 border border-brand-gold/20 hover:border-brand-gold">
                Voir les {property?.images?.length} photos
              </button>
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {property?.description && (
        <div className="px-8 mb-14 relative z-10">
          <h3 className="font-heading font-bold text-lg text-white/80 uppercase tracking-widest mb-6">Description détaillée</h3>
          <div className="text-base text-white/60 leading-relaxed font-light space-y-4 max-w-4xl">
            {property?.description?.split('\n')?.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      {property?.features && Object.keys(property?.features)?.length > 0 && (
        <div className="px-8 mb-14 relative z-10">
          <h3 className="font-heading font-bold text-lg text-white/80 uppercase tracking-widest mb-6">Prestations & Équipements</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(property?.features)?.map(([key, value]) => {
              if (!value) return null;
              
              const getFeatureDetails = (key) => {
                const map = {
                  balcony: { icon: <Home size={18}/>, label: 'Balcon' },
                  elevator: { icon: <ArrowUp size={18}/>, label: 'Ascenseur' },
                  parking: { icon: <Car size={18}/>, label: 'Parking' },
                  pool: { icon: <Droplets size={18}/>, label: 'Piscine' },
                  garden: { icon: <Trees size={18}/>, label: 'Jardin' },
                  security: { icon: <Shield size={18}/>, label: 'Sécurité 24/7' },
                  generator: { icon: <Zap size={18}/>, label: 'Générateur' },
                  water_tank: { icon: <Droplets size={18}/>, label: "Château d'eau" },
                  internet: { icon: <Wifi size={18}/>, label: 'Fibre Optique' },
                  air_conditioning: { icon: <Wind size={18}/>, label: 'Climatisation' }
                };
                return map[key] || { icon: <Check size={18}/>, label: key };
              };

              const feature = getFeatureDetails(key);
              
              return (
                <div key={key} className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border-none">
                  <div className="text-brand-gold bg-brand-gold/10 p-2 rounded-xl">
                    {feature.icon}
                  </div>
                  <span className="text-sm text-white/80 font-medium">{feature.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info & Réservation Sécurisée */}
      <div className="px-8 pb-10 relative z-10">
        
        {/* Informations réglementaires */}
        <h3 className="font-heading font-bold text-lg text-white/80 uppercase tracking-widest mb-6">
          Informations réglementaires
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-1 text-sm bg-white/5 p-8 rounded-3xl border-none mb-10">
          <div className="flex justify-between items-center py-3 border-b border-white/5">
            <span className="text-white/40 font-light">Référence KPS</span>
            <span className="font-mono text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-lg text-xs font-bold">#{property?.id?.slice(-8)?.toUpperCase()}</span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-white/5">
            <span className="text-white/40 font-light">Statut actuel</span>
            <span className="font-medium text-white capitalize">{statusBadge?.text}</span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-white/5">
            <span className="text-white/40 font-light">Type de transaction</span>
            <span className="font-medium text-white">{getMarketTypeLabel(property?.market_type)}</span>
          </div>
          {property?.communes && (
            <div className="flex justify-between items-center py-3 border-b border-white/5 md:border-none">
              <span className="text-white/40 font-light">Zone géographique</span>
              <span className="font-medium text-white">{property?.communes?.name}</span>
            </div>
          )}
        </div>
        
        {/* BLOC DE RÉSERVATION KPS CREATOR */}
        {property?.status === 'available' && (
          <div className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-8 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-gold/5 to-transparent opacity-50" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
              <div className="w-16 h-16 bg-brand-gold/10 rounded-2xl flex flex-shrink-0 items-center justify-center border border-brand-gold/30 shadow-[0_0_20px_rgba(212,175,55,0.15)]">
                <Lock size={28} className="text-brand-gold" />
              </div>
              
              <div className="flex-1">
                <h4 className="font-heading font-bold text-white text-xl mb-2">
                  Sécurisation par KPS Creator requise
                </h4>
                <p className="text-white/50 text-sm font-light leading-relaxed mb-4">
                  Pour garantir la sécurité absolue de votre transaction et éviter toute fraude immobilière, 
                  la réservation en séquestre de ce bien (Réf: #{property?.id?.slice(-8)?.toUpperCase()}) ne peut s'effectuer 
                  que depuis notre environnement bureau chiffré.
                </p>
                
                {/* Le composant de téléchargement autonome */}
                <div className="inline-block">
                  <DownloadKpsCreator />
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PropertyDetails;